import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import CalendarPage from './CalendarPage';
import SlotGrid from '../components/booking/SlotGrid';
import api from '../utils/api';

// LESSON_SUITE_V1 — role-aware booking: student face (Book / My lessons / Schedule)
// + trainer face (Requests / Availability / Setup). Slot-grid booking, no raw date inputs.

const BookingTabIcon = ({ name, size = 18 }) => {
  const base = {
    viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round', strokeLinejoin: 'round', width: size, height: size, 'aria-hidden': true,
  };
  switch (name) {
    case 'calendar':
      return <svg {...base}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/><path d="M8 14h.01M12 14h.01M16 14h.01"/></svg>;
    case 'requests':
      return <svg {...base}><path d="M4 4h16v15H7l-3 3Z"/><path d="M8 9h8M8 13h5"/></svg>;
    case 'book':
      return <svg {...base}><path d="M6 3h11a2 2 0 0 1 2 2v16H8a3 3 0 0 1-3-3V4a1 1 0 0 1 1-1Z"/><path d="M8 17h11"/></svg>;
    case 'horse':
      return <svg {...base}><path d="M7 21h10"/><path d="M8 21c0-3 .5-5 2.5-7L9 9l4-5 1 4 4-2-1 5c2 1.5 3 3.5 3 6"/><path d="M10.5 14c2 1.3 4.7 1.2 6.5-.5"/></svg>;
    case 'clock':
      return <svg {...base}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
    case 'gear':
      return <svg {...base}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.01a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55h.01a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.01a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1Z"/></svg>;
    case 'ribbon':
      return <svg {...base}><circle cx="12" cy="8" r="5"/><path d="M8.5 12.5 7 21l5-3 5 3-1.5-8.5"/></svg>;
    default:
      return null;
  }
};

const LOCATION_TYPES = [
  ['at_barn', 'At barn / facility'],
  ['haul_in', 'Haul-in lesson'],
  ['trainer_travel', 'Trainer travels to you'],
  ['offsite_event', 'Show / clinic / off-site'],
  ['virtual', 'Virtual lesson'],
];

const SERVICE_TYPES = [
  ['private_lesson', 'Private lesson'],
  ['group_lesson', 'Group lesson'],
  ['training_ride', 'Training session / ride'],
  ['groundwork', 'Groundwork'],
  ['show_prep', 'Show prep'],
  ['clinic', 'Clinic / group event'],
  ['virtual_lesson', 'Virtual lesson'],
];

const DOC_TYPES = [
  ['liability_release', 'Liability release'],
  ['coggins', 'Current Coggins'],
  ['health_certificate', 'Health certificate'],
  ['vaccination_record', 'Vaccination record'],
];

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const nowDate = () => new Date().toISOString().slice(0, 10);
const fmtDateTime = (value) => value ? new Date(value).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const Field = ({ label, children, hint }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    {children}
    {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
  </div>
);

const SmallCard = ({ children, style }) => (
  <div className="card" style={{ padding: 12, marginBottom: 10, ...style }}>{children}</div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      border: active ? '1.5px solid var(--cr-primary)' : '1px solid var(--border-color)',
      background: active ? 'var(--cr-primary)' : '#fff',
      color: active ? '#F5EBDD' : 'var(--text-primary)',
      borderRadius: 20, padding: '7px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    }}
  >{children}</button>
);

function routeStatusStyle(status) {
  if (status === 'conflict') return { background: '#FEE2E2', color: '#991B1B' };
  if (status === 'tight') return { background: '#FEF3C7', color: '#92400E' };
  if (status === 'works' || status === 'google') return { background: 'var(--cr-green-light)', color: 'var(--cr-green-dark)' };
  return { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' };
}

const STATUS_BADGE = {
  requested: ['badge-amber', 'Awaiting trainer'],
  alternate_suggested: ['badge-purple', 'New time suggested'],
  confirmed: ['badge-green', 'Confirmed'],
  auto_confirmed: ['badge-green', 'Confirmed'],
  declined: ['badge-red', 'Declined'],
  cancelled: ['badge-red', 'Cancelled'],
  release_required: ['badge-amber', 'Release needed'],
};

const MeetingLinkEditor = ({ request, onSaved }) => {
  const [link, setLink] = useState(request.meeting_link || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/booking/requests/${request.id}/meeting-link`, { meeting_link: link });
      toast.success(link ? 'Video link saved' : 'Video link removed');
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save link'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
      <input className="form-input" style={{ fontSize: 12 }} placeholder="Video call link (Zoom, Meet...)" value={link} onChange={e => setLink(e.target.value)} />
      <button className="btn btn-secondary btn-sm" disabled={busy} onClick={save}>Save</button>
    </div>
  );
};

const CalendarBookingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('book');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [samples, setSamples] = useState([]);
  const [checklistSamples, setChecklistSamples] = useState([]);
  const [routeResult, setRouteResult] = useState(null);

  // Book flow state
  const [trainers, setTrainers] = useState([]);
  const [trainersLoading, setTrainersLoading] = useState(true);
  const [selTrainerId, setSelTrainerId] = useState('');
  const [selServiceId, setSelServiceId] = useState('');
  const [pickedSlot, setPickedSlot] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ horse_id: '', horse_name_text: '', location_type: 'at_barn', location_id: '', student_notes: '', policy_acknowledged: false });
  const [serviceChecklist, setServiceChecklist] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  // Trainer suggest-alternate state
  const [suggestFor, setSuggestFor] = useState(null);

  // Setup forms (trainer)
  const [setupSection, setSetupSection] = useState('services');
  const [locationForm, setLocationForm] = useState({ owner_type: 'student', location_name: '', address_line_1: '', city: '', state: 'TX', zip: '', gate_code: '', parking_notes: '', arena_notes: '', is_primary: false });
  const [serviceForm, setServiceForm] = useState({
    service_name: 'Private Lesson', service_type: 'private_lesson', duration_minutes: 60, capacity: 1, is_group_service: false,
    bookable_by_barn_members: true, bookable_publicly: false, requires_approval: true, auto_accept_barn_members: false,
    buffer_before_minutes: 10, buffer_after_minutes: 10, default_location_type: 'at_barn', require_release: true, require_policy_ack: true,
    reminder_offsets_minutes: '1440,120', release_gate_mode: 'block', min_notice_hours: 2,
  });
  const [policyForm, setPolicyForm] = useState({ title: '', policy_text: '', late_cancel_hours: 24, is_default: true });
  const [availabilityForm, setAvailabilityForm] = useState({ day_of_week: 1, start_time: '08:00', end_time: '12:00', is_available: true });
  const [exceptionForm, setExceptionForm] = useState({ starts_at: `${nowDate()}T08:00`, ends_at: `${nowDate()}T17:00`, reason: '' });
  const [requirementForm, setRequirementForm] = useState({ location_type: 'haul_in', document_type: 'coggins', is_required: true, valid_for_days: 365, requires_approval: true, blocks_booking_if_missing: true });
  const [routeForm, setRouteForm] = useState({ from_location_id: '', to_location_id: '', departure_time: '' });
  const [checklists, setChecklists] = useState([]);
  const [checklistForm, setChecklistForm] = useState({ id: null, title: 'Before your lesson', service_id: '', items: [] });
  const [checklistNewItem, setChecklistNewItem] = useState('');
  const [privacy, setPrivacy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [boot, samplePolicies] = await Promise.all([
        api.get('/booking/bootstrap'),
        api.get('/booking/samples/cancellation-policies'),
      ]);
      setData(boot.data);
      setSamples(samplePolicies.data);
      if (boot.data?.is_trainer) setActiveTab(prev => prev === 'book' ? 'requests' : prev);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load booking tools');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrainers = useCallback(async () => {
    setTrainersLoading(true);
    try {
      const resp = await api.get('/booking/trainers');
      const list = resp.data.trainers || [];
      setTrainers(list);
      if (list.length === 1) {
        setSelTrainerId(list[0].id);
        if (list[0].services.length === 1) setSelServiceId(list[0].services[0].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load trainers');
    } finally {
      setTrainersLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadTrainers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const me = data?.user;
  const isTrainer = !!data?.is_trainer;
  const organizations = data?.organizations || [];
  const barnId = organizations[0]?.id || null;
  const myServices = data?.services || [];
  const locations = data?.locations || [];
  const requests = data?.requests || [];
  const policies = data?.policies || [];
  const availability = data?.availability || [];
  const exceptions = data?.exceptions || [];
  const requirements = data?.requirements || [];
  const horses = data?.horses || [];

  const selTrainer = trainers.find(t => t.id === selTrainerId) || null;
  const selService = selTrainer?.services.find(s => s.id === selServiceId) || null;

  const myLessons = useMemo(() => requests.filter(r => r.requester_user_id === me?.id), [requests, me]);
  const trainerRequests = useMemo(() => requests.filter(r => r.trainer_user_id === me?.id && r.requester_user_id !== me?.id), [requests, me]);
  const trainerPending = trainerRequests.filter(r => ['requested', 'alternate_suggested', 'release_required'].includes(r.status));
  const trainerConfirmed = trainerRequests.filter(r => ['confirmed', 'auto_confirmed'].includes(r.status));

  const TABS = useMemo(() => {
    const t = [['book', 'Book', 'book'], ['mylessons', 'My lessons', 'ribbon'], ['schedule', 'Schedule', 'calendar']];
    if (isTrainer) t.splice(1, 0, ['requests', 'Requests', 'requests']);
    if (isTrainer) t.push(['availability', 'Availability', 'clock'], ['setup', 'Setup', 'gear']);
    return t;
  }, [isTrainer]);

  const reloadQuiet = async () => {
    const boot = await api.get('/booking/bootstrap');
    setData(boot.data);
  };

  // ---------- Book flow ----------
  const openConfirm = async (slot) => {
    setPickedSlot(slot);
    setEligibility(null);
    setServiceChecklist(null);
    const defLoc = selService?.default_location_type || 'at_barn';
    setConfirmForm({ horse_id: horses[0]?.id || '', horse_name_text: '', location_type: defLoc, location_id: '', student_notes: '', policy_acknowledged: false });
    try {
      const [elig, chk] = await Promise.all([
        api.post('/booking/eligibility', { barn_id: barnId, trainer_user_id: selTrainerId, service_id: selServiceId, horse_id: horses[0]?.id || null, location_type: defLoc }),
        api.get(`/booking/checklists/for-service/${selServiceId}`),
      ]);
      setEligibility(elig.data);
      setServiceChecklist(chk.data);
    } catch (e) { /* non-blocking */ }
  };

  const submitBooking = async () => {
    if (!pickedSlot || !selService) return;
    if (selService.require_policy_ack !== false && !confirmForm.policy_acknowledged) return toast.error('Please acknowledge the cancellation policy');
    setSaving(true);
    try {
      await api.post('/booking/requests', {
        barn_id: barnId,
        service_id: selServiceId,
        horse_id: confirmForm.horse_id || null,
        location_type: confirmForm.location_type,
        location_id: confirmForm.location_id || null,
        requested_start_at: pickedSlot.start,
        requested_end_at: pickedSlot.end,
        horse_name_text: confirmForm.horse_name_text || undefined,
        student_notes: confirmForm.student_notes,
        policy_acknowledged: confirmForm.policy_acknowledged,
      });
      const auto = selService.auto_accept_barn_members && !selService.requires_approval;
      toast.success(auto ? 'Booked & confirmed!' : 'Request sent — the trainer will confirm');
      setPickedSlot(null);
      setActiveTab('mylessons');
      await reloadQuiet();
    } catch (e) {
      const code = e.response?.data?.error;
      if (code === 'release_required') toast.error('A current signed release is required first');
      else if (code === 'documents_required') toast.error('Required documents are missing or expired');
      else toast.error(e.response?.data?.message || 'Failed to book');
    } finally { setSaving(false); }
  };

  const studentRespond = async (r, action) => {
    setSaving(true);
    try {
      await api.put(`/booking/requests/${r.id}/student-response`, { action });
      toast.success(action === 'accept_alternate' ? 'New time accepted' : 'Suggestion declined');
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const cancelLesson = async (r) => {
    if (!window.confirm('Cancel this lesson?')) return;
    setSaving(true);
    try {
      await api.put(`/booking/requests/${r.id}/cancel`, {});
      toast.success('Lesson cancelled');
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to cancel'); }
    finally { setSaving(false); }
  };

  // ---------- Trainer requests ----------
  const respond = async (request, action, extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/booking/requests/${request.id}/respond`, { action, ...extra });
      toast.success('Response saved');
      setSuggestFor(null);
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to respond'); }
    finally { setSaving(false); }
  };

  const declineWithNote = (request) => {
    const note = window.prompt('Optional decline reason:') || '';
    respond(request, 'decline', { note });
  };

  // ---------- Setup: services/policies/etc (trainer) ----------
  const setSvc = (field, value) => {
    setServiceForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'service_type') {
        next.is_group_service = value === 'group_lesson' || value === 'clinic';
        next.capacity = next.is_group_service ? Math.max(2, Number(prev.capacity) || 4) : 1;
        if (value === 'virtual_lesson') next.default_location_type = 'virtual';
      }
      if (field === 'is_group_service') next.capacity = value ? Math.max(2, Number(prev.capacity) || 4) : 1;
      return next;
    });
  };

  const saveService = async () => {
    if (!serviceForm.service_name.trim()) return toast.error('Service name required');
    setSaving(true);
    try {
      const reminders = String(serviceForm.reminder_offsets_minutes || '').split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n));
      await api.post('/booking/services', { ...serviceForm, barn_id: barnId || null, reminder_offsets_minutes: reminders });
      toast.success('Service saved');
      setServiceForm(p => ({ ...p, service_name: '' }));
      await reloadQuiet();
      await loadTrainers();
    } catch (e) { toast.error(e.response?.data?.error || e.response?.data?.message || 'Failed to save service'); }
    finally { setSaving(false); }
  };

  const savePolicy = async () => {
    if (!policyForm.title.trim() || !policyForm.policy_text.trim()) return toast.error('Policy title and text required');
    setSaving(true);
    try {
      await api.post('/booking/policies', { ...policyForm, barn_id: barnId || null });
      toast.success('Policy saved');
      setPolicyForm({ title: '', policy_text: '', late_cancel_hours: 24, is_default: false });
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save policy'); }
    finally { setSaving(false); }
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await api.post('/booking/availability/rules', { ...availabilityForm, barn_id: barnId || null });
      toast.success('Availability saved');
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save availability'); }
    finally { setSaving(false); }
  };

  const saveException = async () => {
    setSaving(true);
    try {
      await api.post('/booking/availability/exceptions', { starts_at: new Date(exceptionForm.starts_at).toISOString(), ends_at: new Date(exceptionForm.ends_at).toISOString(), reason: exceptionForm.reason, blocks_public_booking: true, public_visibility: 'unavailable_only', barn_id: barnId || null });
      toast.success('Time blocked');
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to block time'); }
    finally { setSaving(false); }
  };

  const saveRequirement = async () => {
    setSaving(true);
    try {
      await api.post('/booking/requirements', { ...requirementForm, barn_id: barnId || null });
      toast.success('Document rule saved');
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save document rule'); }
    finally { setSaving(false); }
  };

  const saveLocation = async () => {
    if (!locationForm.location_name.trim()) return toast.error('Location name required');
    setSaving(true);
    try {
      await api.post('/booking/locations', { ...locationForm, barn_id: barnId || null });
      setLocationForm({ owner_type: 'student', location_name: '', address_line_1: '', city: '', state: 'TX', zip: '', gate_code: '', parking_notes: '', arena_notes: '', is_primary: false });
      toast.success('Location saved');
      await reloadQuiet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save location'); }
    finally { setSaving(false); }
  };

  const runRouteEstimate = async () => {
    if (!routeForm.from_location_id || !routeForm.to_location_id) return toast.error('Choose two locations');
    setSaving(true);
    try {
      const { data: result } = await api.post('/booking/route/estimate', { ...routeForm, departure_time: routeForm.departure_time ? new Date(routeForm.departure_time).toISOString() : '' });
      setRouteResult(result);
    } catch (e) { toast.error(e.response?.data?.message || 'Route estimate failed'); }
    finally { setSaving(false); }
  };

  // ---------- Checklists ----------
  const loadChecklists = useCallback(async () => {
    try {
      const [mine, tmpl] = await Promise.all([api.get('/booking/checklists'), api.get('/booking/samples/checklists')]);
      setChecklists(mine.data);
      setChecklistSamples(tmpl.data);
    } catch (e) { /* silent */ }
  }, []);

  const loadPrivacy = useCallback(async () => {
    try {
      const resp = await api.get('/booking/calendar-settings');
      setPrivacy(resp.data.privacy);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (activeTab === 'setup') { loadChecklists(); loadPrivacy(); }
  }, [activeTab, loadChecklists, loadPrivacy]);

  const applyChecklistTemplate = (tmpl) => {
    setChecklistForm(p => ({ ...p, title: tmpl.title, items: [...tmpl.items] }));
  };

  const saveChecklist = async () => {
    if (!checklistForm.items.length) return toast.error('Add at least one item');
    setSaving(true);
    try {
      if (checklistForm.id) {
        await api.put(`/booking/checklists/${checklistForm.id}`, { title: checklistForm.title, service_id: checklistForm.service_id || null, items: checklistForm.items });
      } else {
        await api.post('/booking/checklists', { title: checklistForm.title, service_id: checklistForm.service_id || null, items: checklistForm.items, barn_id: barnId || null });
      }
      toast.success('Checklist saved');
      setChecklistForm({ id: null, title: 'Before your lesson', service_id: '', items: [] });
      await loadChecklists();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save checklist'); }
    finally { setSaving(false); }
  };

  const deleteChecklist = async (id) => {
    if (!window.confirm('Remove this checklist?')) return;
    try {
      await api.delete(`/booking/checklists/${id}`);
      await loadChecklists();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const savePrivacy = async (patch) => {
    const next = { ...privacy, ...patch };
    setPrivacy(next);
    try {
      await api.put('/booking/calendar-settings', { privacy: next });
      toast.success('Privacy updated');
    } catch (e) { toast.error('Failed to save privacy'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const serviceLabel = (s) => `${s.service_name} · ${s.duration_minutes} min${s.capacity > 1 ? ` · ${s.capacity} spots` : ''}`;

  return (
    <div>
      <style>{`.calendar-booking-tabs::-webkit-scrollbar { display: none; }`}</style>

      <div style={{ background: 'var(--cr-primary)', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 21, lineHeight: 1.15, fontWeight: 700, color: '#F5EBDD' }}>
            Lessons & Booking
          </div>
          <div style={{ color: 'rgba(245,235,221,.80)', fontSize: 11, marginTop: 4 }}>
            {isTrainer ? 'Your students, schedule & services' : 'Book with your barn\u2019s trainers'}
          </div>
        </div>
        <button
          onClick={() => navigate('/calendar/add')}
          style={{ height: 38, border: 'none', borderRadius: 11, background: '#F5EBDD', color: 'var(--cr-primary)', padding: '0 15px', fontSize: 12, fontWeight: 750, cursor: 'pointer', flexShrink: 0 }}
        >+ Add event</button>
      </div>

      <div className="calendar-booking-tabs" style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', background: '#fff', borderBottom: '1px solid var(--border-color)' }}>
        {TABS.map(([key, label, icon]) => {
          const active = activeTab === key;
          const badge = key === 'requests' && trainerPending.length > 0 ? trainerPending.length : null;
          return (
            <button key={key} onClick={() => setActiveTab(key)} style={{
              minWidth: 82, height: 52, padding: '0 9px', border: 'none',
              borderBottom: active ? '2px solid var(--cr-primary)' : '2px solid transparent',
              background: 'transparent', color: active ? 'var(--cr-primary)' : 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontSize: 11, fontWeight: active ? 750 : 600, whiteSpace: 'nowrap', cursor: 'pointer', position: 'relative',
            }}>
              <BookingTabIcon name={icon} size={17} />
              {label}
              {badge && <span style={{ background: '#D4537E', color: '#fff', borderRadius: 9, fontSize: 10, fontWeight: 800, padding: '1px 6px', marginLeft: 2 }}>{badge}</span>}
            </button>
          );
        })}
      </div>

      <div className="page-content" style={{ paddingBottom: 90 }}>

        {activeTab === 'schedule' && <CalendarPage />}

        {/* ================= BOOK (student flow) ================= */}
        {activeTab === 'book' && !pickedSlot && (
          <>
            {trainersLoading && <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" /></div>}
            {!trainersLoading && trainers.length === 0 && (
              <SmallCard>
                <div style={{ fontWeight: 700 }}>No bookable trainers yet</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Your barn owner or manager can turn on "offers lessons" for trainers in barn settings — then trainers appear here with their open times.
                </div>
                {isTrainer && <button className="btn btn-primary btn-full" style={{ marginTop: 10 }} onClick={() => setActiveTab('setup')}>Set up your services</button>}
              </SmallCard>
            )}

            {trainers.length > 0 && (
              <>
                <div className="section-label">Trainer</div>
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
                  {trainers.map(t => (
                    <Chip key={t.id} active={t.id === selTrainerId} onClick={() => { setSelTrainerId(t.id); setSelServiceId(t.services.length === 1 ? t.services[0].id : ''); }}>
                      {t.name}{t.is_me ? ' (you)' : ''}
                    </Chip>
                  ))}
                </div>

                {selTrainer && (
                  <>
                    <div className="section-label">Service</div>
                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
                      {selTrainer.services.map(s => (
                        <Chip key={s.id} active={s.id === selServiceId} onClick={() => setSelServiceId(s.id)}>{serviceLabel(s)}</Chip>
                      ))}
                    </div>
                  </>
                )}

                {selService && (
                  <>
                    <div className="section-label">Tap an open time</div>
                    <SlotGrid trainerId={selTrainerId} serviceId={selServiceId} onPick={openConfirm} />
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Confirm sheet */}
        {activeTab === 'book' && pickedSlot && selService && (
          <>
            <SmallCard style={{ borderLeft: '4px solid var(--cr-green)' }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{fmtDateTime(pickedSlot.start)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {selService.service_name} · {selService.duration_minutes} min · with {selTrainer?.name}
              </div>
            </SmallCard>

            <SmallCard>
              <Field label="Horse">
                <select className="form-input" value={confirmForm.horse_id} onChange={e => setConfirmForm(p => ({ ...p, horse_id: e.target.value }))}>
                  <option value="">No horse / school horse</option>
                  {horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </Field>
              <Field label="Lesson type">
                <select className="form-input" value={confirmForm.location_type} onChange={e => setConfirmForm(p => ({ ...p, location_type: e.target.value }))}>
                  {LOCATION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              {confirmForm.location_type === 'haul_in' && (
                <Field label="Horse you're hauling in" hint="If your horse isn't in this barn's list">
                  <input className="form-input" value={confirmForm.horse_name_text} onChange={e => setConfirmForm(p => ({ ...p, horse_name_text: e.target.value }))} placeholder="Horse name" />
                </Field>
              )}
              {['trainer_travel', 'offsite_event'].includes(confirmForm.location_type) && (
                <Field label="Location" hint="Where should the trainer come?">
                  <select className="form-input" value={confirmForm.location_id} onChange={e => setConfirmForm(p => ({ ...p, location_id: e.target.value }))}>
                    <option value="">Select saved location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}{l.city ? ` · ${l.city}` : ''}</option>)}
                  </select>
                </Field>
              )}
              {confirmForm.location_type === 'virtual' && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  📱 Virtual lesson — your trainer will share a video link once confirmed.
                </div>
              )}
              <Field label="Notes / goals">
                <textarea className="form-input" rows={2} value={confirmForm.student_notes} onChange={e => setConfirmForm(p => ({ ...p, student_notes: e.target.value }))} placeholder="What do you want to work on?" />
              </Field>

              {eligibility && !eligibility.ok && (
                <div style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 8, padding: '8px 10px', fontSize: 12, marginBottom: 10 }}>
                  <b>Before you can book:</b>
                  <div style={{ marginTop: 3 }}>Release: {eligibility.release?.status?.replace(/_/g, ' ') || 'unknown'}</div>
                  {eligibility.documents?.filter(d => !d.ok).map(d => <div key={d.document_type}>✗ {d.document_type.replace(/_/g, ' ')}</div>)}
                </div>
              )}
              {eligibility?.ok && (
                <div style={{ background: 'var(--cr-green-light)', color: 'var(--cr-green-dark)', borderRadius: 8, padding: '8px 10px', fontSize: 12, marginBottom: 10 }}>
                  ✓ Release & documents look current
                </div>
              )}

              {serviceChecklist?.items?.length > 0 && (
                <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 5 }}>📋 {serviceChecklist.title}</div>
                  {serviceChecklist.items.map((item, i) => <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>• {item}</div>)}
                </div>
              )}

              {selService.require_policy_ack !== false && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 4 }}>
                  <input type="checkbox" checked={confirmForm.policy_acknowledged} onChange={e => setConfirmForm(p => ({ ...p, policy_acknowledged: e.target.checked }))} style={{ marginTop: 2, accentColor: 'var(--cr-green)' }} />
                  <span>I acknowledge the trainer's cancellation policy.</span>
                </label>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-secondary" onClick={() => setPickedSlot(null)}>← Back to times</button>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={submitBooking}>
                  {saving ? 'Booking…' : (selService.auto_accept_barn_members && !selService.requires_approval ? 'Book this time' : 'Request this time')}
                </button>
              </div>
            </SmallCard>
          </>
        )}

        {/* ================= MY LESSONS (student) ================= */}
        {activeTab === 'mylessons' && (
          <>
            {myLessons.length === 0 && (
              <SmallCard>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No lessons yet.</div>
                <button className="btn btn-primary btn-full" style={{ marginTop: 10 }} onClick={() => setActiveTab('book')}>Book your first lesson</button>
              </SmallCard>
            )}
            {myLessons.map(r => {
              const [badgeCls, badgeLabel] = STATUS_BADGE[r.status] || ['badge-amber', r.status];
              return (
                <SmallCard key={r.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtDateTime(r.requested_start_at)}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.location_type?.replace(/_/g, ' ')}{r.horse_name_text ? ` · ${r.horse_name_text}` : ''}</div>
                    </div>
                    <span className={`badge ${badgeCls}`}>{badgeLabel}</span>
                  </div>
                  {r.status === 'alternate_suggested' && (
                    <div style={{ background: '#F3E8FF', borderRadius: 8, padding: '8px 10px', marginTop: 8 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6B21A8' }}>Trainer suggests: {fmtDateTime(r.suggested_start_at)}</div>
                      {r.trainer_response_note && <div style={{ fontSize: 12, marginTop: 3 }}>{r.trainer_response_note}</div>}
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => studentRespond(r, 'accept_alternate')}>Accept new time</button>
                        <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => studentRespond(r, 'decline_alternate')}>Decline</button>
                      </div>
                    </div>
                  )}
                  {r.meeting_link && ['confirmed', 'auto_confirmed'].includes(r.status) && (
                    <a href={r.meeting_link} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--cr-green-dark)' }}>📱 Join video lesson</a>
                  )}
                  {['requested', 'confirmed', 'auto_confirmed', 'alternate_suggested'].includes(r.status) && (
                    <div style={{ marginTop: 8 }}>
                      <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => cancelLesson(r)}>Cancel lesson</button>
                    </div>
                  )}
                </SmallCard>
              );
            })}
          </>
        )}

        {/* ================= REQUESTS (trainer) ================= */}
        {activeTab === 'requests' && isTrainer && (
          <>
            <div className="section-label">Pending ({trainerPending.length})</div>
            {trainerPending.length === 0 && <SmallCard><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No pending requests. New requests will appear here.</div></SmallCard>}
            {trainerPending.map(r => {
              const svc = myServices.find(s => s.id === r.service_id);
              return (
                <SmallCard key={r.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14 }}>{r.student_name || 'Student'}</div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2 }}>{fmtDateTime(r.requested_start_at)}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {svc?.service_name || 'Lesson'} · {r.location_type?.replace(/_/g, ' ')}
                        {r.horse_name_text ? ` · 🐴 ${r.horse_name_text}` : ''}
                      </div>
                    </div>
                    <span className="badge badge-amber">{r.status === 'alternate_suggested' ? 'awaiting student' : 'new'}</span>
                  </div>
                  {r.release_status_at_booking && r.release_status_at_booking !== 'current' && r.release_status_at_booking !== 'not_required' && (
                    <div style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 8, padding: '6px 9px', marginTop: 8, fontSize: 12 }}>
                      ⚠ Release: {String(r.release_status_at_booking).replace(/_/g, ' ')}
                    </div>
                  )}
                  {r.route_recommendation?.status && (
                    <div style={{ ...routeStatusStyle(r.route_recommendation.status), padding: '7px 9px', borderRadius: 8, marginTop: 8, fontSize: 12 }}>
                      🚚 Route: {r.route_recommendation.status}. {r.route_recommendation.recommendations?.[0]?.label || ''}
                    </div>
                  )}
                  {r.student_notes && <div style={{ fontSize: 12, marginTop: 8, fontStyle: 'italic' }}>"{r.student_notes}"</div>}
                  {r.status === 'alternate_suggested' && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>You suggested: {fmtDateTime(r.suggested_start_at)}</div>}

                  {suggestFor === r.id ? (
                    <div style={{ marginTop: 10, borderTop: '1px solid var(--border-color)', paddingTop: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 8 }}>Tap a time to suggest instead:</div>
                      <SlotGrid trainerId={me.id} serviceId={r.service_id} onPick={(slot) => respond(r, 'suggest_alternate', { suggested_start_at: slot.start })} />
                      <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => setSuggestFor(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                      <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => respond(r, 'accept')}>✓ Accept</button>
                      <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => setSuggestFor(r.id)}>Suggest time</button>
                      <button className="btn btn-danger btn-sm" disabled={saving} onClick={() => declineWithNote(r)}>Decline</button>
                    </div>
                  )}
                </SmallCard>
              );
            })}

            <div className="section-label">Confirmed</div>
            {trainerConfirmed.length === 0 && <SmallCard><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No confirmed lessons.</div></SmallCard>}
            {trainerConfirmed.map(r => (
              <SmallCard key={r.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{r.student_name || 'Student'} · {fmtDateTime(r.requested_start_at)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{myServices.find(s => s.id === r.service_id)?.service_name || 'Lesson'}{r.horse_name_text ? ` · ${r.horse_name_text}` : ''}</div>
                  </div>
                  <span className="badge badge-green">confirmed</span>
                </div>
                {r.location_type === 'virtual' && <MeetingLinkEditor request={r} onSaved={reloadQuiet} />}
              </SmallCard>
            ))}
          </>
        )}

        {/* ================= AVAILABILITY (trainer) ================= */}
        {activeTab === 'availability' && isTrainer && (
          <>
            <SmallCard>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Weekly teaching hours</div>
              <Field label="Day"><select className="form-input" value={availabilityForm.day_of_week} onChange={e => setAvailabilityForm(p => ({ ...p, day_of_week: Number(e.target.value) }))}>{DAY_LABELS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Start"><input className="form-input" type="time" value={availabilityForm.start_time} onChange={e => setAvailabilityForm(p => ({ ...p, start_time: e.target.value }))} /></Field>
                <Field label="End"><input className="form-input" type="time" value={availabilityForm.end_time} onChange={e => setAvailabilityForm(p => ({ ...p, end_time: e.target.value }))} /></Field>
              </div>
              <button className="btn btn-primary btn-full" disabled={saving} onClick={saveAvailability}>Add weekly hours</button>
            </SmallCard>

            <SmallCard>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Block time off</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>Shows as "Unavailable" to students — never the reason.</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Starts"><input className="form-input" type="datetime-local" value={exceptionForm.starts_at} onChange={e => setExceptionForm(p => ({ ...p, starts_at: e.target.value }))} /></Field>
                <Field label="Ends"><input className="form-input" type="datetime-local" value={exceptionForm.ends_at} onChange={e => setExceptionForm(p => ({ ...p, ends_at: e.target.value }))} /></Field>
              </div>
              <Field label="Reason (private, only you see this)"><input className="form-input" value={exceptionForm.reason} onChange={e => setExceptionForm(p => ({ ...p, reason: e.target.value }))} placeholder="Horse show, vacation, personal" /></Field>
              <button className="btn btn-secondary btn-full" disabled={saving} onClick={saveException}>Block this time</button>
            </SmallCard>

            <div className="section-label">Current weekly hours</div>
            {availability.length === 0 && <SmallCard><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No teaching hours yet — students can't see open times until you add some.</div></SmallCard>}
            {availability.map(a => <SmallCard key={a.id}><div style={{ fontWeight: 700 }}>{DAY_LABELS[a.day_of_week]}</div><div style={{ fontSize: 12 }}>{a.start_time} – {a.end_time}</div></SmallCard>)}

            <div className="section-label">Blocked time</div>
            {exceptions.map(e => <SmallCard key={e.id}><div style={{ fontWeight: 700 }}>{e.reason || 'Blocked'}</div><div style={{ fontSize: 12 }}>{fmtDateTime(e.starts_at)} – {fmtDateTime(e.ends_at)}</div></SmallCard>)}
          </>
        )}

        {/* ================= SETUP (trainer) ================= */}
        {activeTab === 'setup' && isTrainer && (
          <>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 12 }}>
              {[['services', 'Services'], ['checklists', 'Checklists'], ['policies', 'Policies'], ['privacy', 'Privacy'], ['docs', 'Doc rules'], ['locations', 'Locations'], ['route', 'Route']].map(([k, l]) => (
                <Chip key={k} active={setupSection === k} onClick={() => setSetupSection(k)}>{l}</Chip>
              ))}
            </div>

            {setupSection === 'services' && (
              <>
                <SmallCard>
                  <Field label="Service name"><input className="form-input" value={serviceForm.service_name} onChange={e => setSvc('service_name', e.target.value)} placeholder="Private Lesson" /></Field>
                  <Field label="Service type"><select className="form-input" value={serviceForm.service_type} onChange={e => setSvc('service_type', e.target.value)}>{SERVICE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Lesson length (min)"><input className="form-input" type="number" step="15" value={serviceForm.duration_minutes} onChange={e => setSvc('duration_minutes', Number(e.target.value))} /></Field>
                    <Field label="Capacity"><input className="form-input" type="number" min="1" value={serviceForm.capacity} onChange={e => setSvc('capacity', Number(e.target.value))} /></Field>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Arrival buffer (min)"><input className="form-input" type="number" value={serviceForm.buffer_before_minutes} onChange={e => setSvc('buffer_before_minutes', Number(e.target.value))} /></Field>
                    <Field label="Departure buffer (min)"><input className="form-input" type="number" value={serviceForm.buffer_after_minutes} onChange={e => setSvc('buffer_after_minutes', Number(e.target.value))} /></Field>
                  </div>
                  <Field label="Minimum booking notice (hours)" hint="Students can't book closer to start than this"><input className="form-input" type="number" value={serviceForm.min_notice_hours} onChange={e => setSvc('min_notice_hours', Number(e.target.value))} /></Field>
                  <Field label="Default lesson type"><select className="form-input" value={serviceForm.default_location_type} onChange={e => setSvc('default_location_type', e.target.value)}>{LOCATION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                  <Field label="If release or documents are missing" hint="Block = student can't book. Warn = booking allowed, you see a warning on the request.">
                    <select className="form-input" value={serviceForm.release_gate_mode} onChange={e => setSvc('release_gate_mode', e.target.value)}>
                      <option value="block">Block booking until current</option>
                      <option value="warn">Warn me, but allow booking</option>
                    </select>
                  </Field>
                  <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 8 }}><input type="checkbox" checked={!!serviceForm.requires_approval} onChange={e => setSvc('requires_approval', e.target.checked)} /> I approve each request</label>
                  <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 8 }}><input type="checkbox" checked={!!serviceForm.auto_accept_barn_members} onChange={e => setSvc('auto_accept_barn_members', e.target.checked)} /> Auto-accept barn members when everything passes</label>
                  <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 8 }}><input type="checkbox" checked={!!serviceForm.require_release} onChange={e => setSvc('require_release', e.target.checked)} /> Require current signed release</label>
                  <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 8 }}><input type="checkbox" checked={!!serviceForm.require_policy_ack} onChange={e => setSvc('require_policy_ack', e.target.checked)} /> Require cancellation policy acknowledgment</label>
                  <Field label="Reminder schedule" hint="Minutes before. 1440 = 1 day, 120 = 2 hours."><input className="form-input" value={serviceForm.reminder_offsets_minutes} onChange={e => setSvc('reminder_offsets_minutes', e.target.value)} /></Field>
                  <button className="btn btn-primary btn-full" disabled={saving} onClick={saveService}>Save service</button>
                </SmallCard>
                <div className="section-label">Your services</div>
                {myServices.map(s => (
                  <SmallCard key={s.id}>
                    <div style={{ fontWeight: 700 }}>{s.service_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {s.service_type?.replace(/_/g, ' ')} · {s.duration_minutes} min · {s.capacity} spot{s.capacity === 1 ? '' : 's'} · {s.release_gate_mode === 'warn' ? 'warn on missing docs' : 'blocks on missing docs'}
                    </div>
                  </SmallCard>
                ))}
              </>
            )}

            {setupSection === 'checklists' && (
              <>
                <SmallCard>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>{checklistForm.id ? 'Edit checklist' : 'New pre-lesson checklist'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>Sent with confirmations and reminders so students arrive ready.</div>
                  <Field label="Start from a template">
                    <select className="form-input" value="" onChange={e => { const t = checklistSamples.find(x => x.title === e.target.value); if (t) applyChecklistTemplate(t); }}>
                      <option value="">Choose template…</option>
                      {checklistSamples.map(t => <option key={t.title} value={t.title}>{t.title}</option>)}
                    </select>
                  </Field>
                  <Field label="Checklist title"><input className="form-input" value={checklistForm.title} onChange={e => setChecklistForm(p => ({ ...p, title: e.target.value }))} /></Field>
                  <Field label="Applies to" hint="Blank = all your lessons; or pick one service">
                    <select className="form-input" value={checklistForm.service_id} onChange={e => setChecklistForm(p => ({ ...p, service_id: e.target.value }))}>
                      <option value="">All my lessons</option>
                      {myServices.map(s => <option key={s.id} value={s.id}>{s.service_name}</option>)}
                    </select>
                  </Field>
                  <div className="form-group">
                    <label className="form-label">Items</label>
                    {checklistForm.items.map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <input className="form-input" value={item} onChange={e => setChecklistForm(p => ({ ...p, items: p.items.map((x, j) => j === i ? e.target.value : x) }))} />
                        <button className="btn btn-secondary btn-sm" onClick={() => setChecklistForm(p => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}>✕</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" value={checklistNewItem} placeholder="Add an item…" onChange={e => setChecklistNewItem(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && checklistNewItem.trim()) { setChecklistForm(p => ({ ...p, items: [...p.items, checklistNewItem.trim()] })); setChecklistNewItem(''); } }} />
                      <button className="btn btn-secondary btn-sm" onClick={() => { if (checklistNewItem.trim()) { setChecklistForm(p => ({ ...p, items: [...p.items, checklistNewItem.trim()] })); setChecklistNewItem(''); } }}>Add</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {checklistForm.id && <button className="btn btn-secondary" onClick={() => setChecklistForm({ id: null, title: 'Before your lesson', service_id: '', items: [] })}>New instead</button>}
                    <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={saveChecklist}>{checklistForm.id ? 'Update checklist' : 'Save checklist'}</button>
                  </div>
                </SmallCard>
                <div className="section-label">Your checklists</div>
                {checklists.length === 0 && <SmallCard><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No checklists yet — start from a template above.</div></SmallCard>}
                {checklists.map(c => (
                  <SmallCard key={c.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 700 }}>{c.title}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setChecklistForm({ id: c.id, title: c.title, service_id: c.service_id || '', items: [...c.items] })}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteChecklist(c.id)}>Remove</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.service_id ? (myServices.find(s => s.id === c.service_id)?.service_name || 'One service') : 'All lessons'} · {c.items.length} items</div>
                  </SmallCard>
                ))}
              </>
            )}

            {setupSection === 'policies' && (
              <>
                <SmallCard>
                  <Field label="Use sample policy"><select className="form-input" value="" onChange={e => { const s = samples.find(x => x.title === e.target.value); if (s) setPolicyForm({ title: s.title, policy_text: s.policy_text, late_cancel_hours: s.late_cancel_hours, is_default: policies.length === 0 }); }}><option value="">Choose sample…</option>{samples.map(s => <option key={s.title} value={s.title}>{s.title}</option>)}</select></Field>
                  <Field label="Policy title"><input className="form-input" value={policyForm.title} onChange={e => setPolicyForm(p => ({ ...p, title: e.target.value }))} /></Field>
                  <Field label="Late cancel hours"><input className="form-input" type="number" value={policyForm.late_cancel_hours} onChange={e => setPolicyForm(p => ({ ...p, late_cancel_hours: Number(e.target.value) }))} /></Field>
                  <Field label="Policy text"><textarea className="form-input" rows={6} value={policyForm.policy_text} onChange={e => setPolicyForm(p => ({ ...p, policy_text: e.target.value }))} /></Field>
                  <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 10 }}><input type="checkbox" checked={policyForm.is_default} onChange={e => setPolicyForm(p => ({ ...p, is_default: e.target.checked }))} /> Make default policy</label>
                  <button className="btn btn-primary btn-full" disabled={saving} onClick={savePolicy}>Save policy</button>
                </SmallCard>
                <div className="section-label">Saved policies</div>
                {policies.map(p => <SmallCard key={p.id}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div style={{ fontWeight: 700 }}>{p.title}</div>{p.is_default && <span className="badge badge-green">Default</span>}</div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.late_cancel_hours} hour late-cancel window</div></SmallCard>)}
              </>
            )}

            {setupSection === 'privacy' && (
              <SmallCard>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>What others see on your calendar</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>Privacy first — everything defaults to "busy only". Personal blocks are never shown in detail, ever.</div>
                {!privacy && <div className="spinner" />}
                {privacy && (
                  <>
                    <Field label="Lessons on your schedule">
                      <select className="form-input" value={privacy.lessons} onChange={e => savePrivacy({ lessons: e.target.value })}>
                        <option value="busy_only">Show as "Booked" only</option>
                        <option value="show_type">Show "Lesson" (no student name)</option>
                      </select>
                    </Field>
                    <Field label="Your horses' appointments (farrier, vet…)">
                      <select className="form-input" value={privacy.horse_appointments} onChange={e => savePrivacy({ horse_appointments: e.target.value })}>
                        <option value="busy_only">Show as "Unavailable"</option>
                        <option value="show_type">Show type ("Farrier", "Vet")</option>
                      </select>
                    </Field>
                    <Field label="Barn events you attend">
                      <select className="form-input" value={privacy.barn_events} onChange={e => savePrivacy({ barn_events: e.target.value })}>
                        <option value="busy_only">Show as "Unavailable"</option>
                        <option value="show_event">Show the event name</option>
                      </select>
                    </Field>
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '9px 11px', fontSize: 12, color: 'var(--text-secondary)' }}>
                      🔒 Personal blocks always show as "Unavailable" — the reason stays yours.
                    </div>
                  </>
                )}
              </SmallCard>
            )}

            {setupSection === 'docs' && (
              <>
                <SmallCard>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Required documents by lesson type</div>
                  <Field label="Lesson type"><select className="form-input" value={requirementForm.location_type} onChange={e => setRequirementForm(p => ({ ...p, location_type: e.target.value }))}>{LOCATION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                  <Field label="Required document"><select className="form-input" value={requirementForm.document_type} onChange={e => setRequirementForm(p => ({ ...p, document_type: e.target.value }))}>{DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                  <Field label="Valid for days"><input className="form-input" type="number" value={requirementForm.valid_for_days || ''} onChange={e => setRequirementForm(p => ({ ...p, valid_for_days: e.target.value ? Number(e.target.value) : null }))} /></Field>
                  <button className="btn btn-secondary btn-full" disabled={saving} onClick={saveRequirement}>Save document rule</button>
                </SmallCard>
                <div className="section-label">Document rules</div>
                {requirements.map(r => <SmallCard key={r.id}><div style={{ fontWeight: 700 }}>{r.location_type?.replace(/_/g, ' ')}</div><div style={{ fontSize: 12 }}>{r.document_type?.replace(/_/g, ' ')} · valid {r.valid_for_days || 'custom'} days</div></SmallCard>)}
              </>
            )}

            {setupSection === 'locations' && (
              <>
                <SmallCard>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>Add saved location</div>
                  <Field label="Location name"><input className="form-input" value={locationForm.location_name} onChange={e => setLocationForm(p => ({ ...p, location_name: e.target.value }))} placeholder="Oak Creek Farm" /></Field>
                  <Field label="Owner type"><select className="form-input" value={locationForm.owner_type} onChange={e => setLocationForm(p => ({ ...p, owner_type: e.target.value }))}><option value="student">Student/client</option><option value="barn">Barn/facility</option><option value="trainer">Trainer work-from</option><option value="event">Show/event venue</option></select></Field>
                  <Field label="Address"><input className="form-input" value={locationForm.address_line_1} onChange={e => setLocationForm(p => ({ ...p, address_line_1: e.target.value }))} /></Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px', gap: 8 }}>
                    <Field label="City"><input className="form-input" value={locationForm.city} onChange={e => setLocationForm(p => ({ ...p, city: e.target.value }))} /></Field>
                    <Field label="State"><input className="form-input" value={locationForm.state} onChange={e => setLocationForm(p => ({ ...p, state: e.target.value }))} /></Field>
                    <Field label="Zip"><input className="form-input" value={locationForm.zip} onChange={e => setLocationForm(p => ({ ...p, zip: e.target.value }))} /></Field>
                  </div>
                  <Field label="Gate / parking notes"><input className="form-input" value={locationForm.parking_notes} onChange={e => setLocationForm(p => ({ ...p, parking_notes: e.target.value }))} /></Field>
                  <label style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 10 }}><input type="checkbox" checked={locationForm.is_primary} onChange={e => setLocationForm(p => ({ ...p, is_primary: e.target.checked }))} /> Use as primary work-from/start location</label>
                  <button className="btn btn-primary btn-full" disabled={saving} onClick={saveLocation}>Save location</button>
                </SmallCard>
                <div className="section-label">Saved locations</div>
                {locations.map(l => <SmallCard key={l.id}><div style={{ display: 'flex', justifyContent: 'space-between' }}><div style={{ fontWeight: 700 }}>{l.location_name}</div>{l.is_primary && <span className="badge badge-green">Primary</span>}</div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{[l.address_line_1, l.city, l.state].filter(Boolean).join(', ')}</div></SmallCard>)}
              </>
            )}

            {setupSection === 'route' && (
              <>
                {!data?.route_api_configured && (
                  <SmallCard style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#9A3412' }}>Google route API key not configured</div>
                    <div style={{ fontSize: 12, color: '#9A3412', marginTop: 4 }}>Drive times use a straight-line estimate until GOOGLE_MAPS_API_KEY is added to the server .env.</div>
                  </SmallCard>
                )}
                <SmallCard>
                  <Field label="From"><select className="form-input" value={routeForm.from_location_id} onChange={e => setRouteForm(p => ({ ...p, from_location_id: e.target.value }))}><option value="">Select start</option>{locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}</select></Field>
                  <Field label="To"><select className="form-input" value={routeForm.to_location_id} onChange={e => setRouteForm(p => ({ ...p, to_location_id: e.target.value }))}><option value="">Select destination</option>{locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}</select></Field>
                  <Field label="Departure time"><input className="form-input" type="datetime-local" value={routeForm.departure_time} onChange={e => setRouteForm(p => ({ ...p, departure_time: e.target.value }))} /></Field>
                  <button className="btn btn-primary btn-full" disabled={saving} onClick={runRouteEstimate}>Estimate drive time</button>
                </SmallCard>
                {routeResult && !routeResult.segments && <SmallCard style={routeStatusStyle(routeResult.status)}><div style={{ fontWeight: 700 }}>{routeResult.drive_minutes || '?'} minutes · {routeResult.distance_miles || '?'} miles</div><div style={{ fontSize: 12 }}>Source: {routeResult.source}</div></SmallCard>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CalendarBookingPage;
